
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Form } from '@/components/ui/form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { supabase } from '@/integrations/supabase/client';
import FormField from './FormField';

const loginSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  password: z.string().min(6, { message: 'Password must be at least 6 characters' }),
});

type LoginFormProps = {
  setLoginAttempts: React.Dispatch<React.SetStateAction<number>>;
  loginAttempts: number;
};

const LoginForm: React.FC<LoginFormProps> = ({ setLoginAttempts, loginAttempts }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);

  // Check for email confirmation query parameter
  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const emailConfirmed = searchParams.get('email_confirmed');
    
    if (emailConfirmed === 'true') {
      // Redirect to the email-confirmed page
      navigate('/email-confirmed', { replace: true });
      
      toast({
        title: 'Email Confirmed',
        description: 'Your email has been confirmed. You can now log in.',
      });
    }
  }, [location, navigate, toast]);

  const loginForm = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const handleLogin = async (values: z.infer<typeof loginSchema>) => {
    setIsLoading(true);
    setErrorMsg(null);
    
    try {
      setLoginAttempts(prev => prev + 1);
      console.log(`Login attempt ${loginAttempts + 1} for email: ${values.email}`);
      
      // 1) Sign in with Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });

      if (error) throw error;
      
      const user = data.user;
      if (!user) {
        throw new Error('Unexpected error: no user data returned.');
      }

      // 2) Check onboarding status
      const { data: onboardingData, error: onboardingError } = await supabase
        .from('client_onboarding')
        .select('status, role, id')
        .eq('email', values.email)
        .maybeSingle();
      
      if (onboardingError) {
        console.error('Error fetching onboarding data:', onboardingError.message);
        throw onboardingError;
      }
      
      if (!onboardingData) {
        console.error('No onboarding data found for email:', values.email);
        throw new Error('Could not retrieve account status. Please contact support.');
      }

      // 3) Gate access by status
      const status = onboardingData.status;
      
      if (status === 'rejected') {
        // Sign the user out
        await supabase.auth.signOut();
        throw new Error('Your account request was rejected. Please contact support.');
      } 
      
      if (status !== 'verified' && status !== 'approved') {
        // Sign the user out
        await supabase.auth.signOut();
        throw new Error('Your email is confirmed, but your account is still awaiting admin approval.');
      }

      // 4) Check if IDs match between auth.users and client_onboarding
      if (user.id !== onboardingData.id) {
        console.warn(`ID mismatch detected: auth.users ID (${user.id}) doesn't match client_onboarding ID (${onboardingData.id})`);
      }

      toast({
        title: 'Login successful',
        description: 'You have been logged in successfully',
      });

      // 5) Redirect based on role - CRITICAL FIX: immediately redirect admins to /admin
      const isAdmin = onboardingData.role === 'admin';
      console.log(`User login successful, isAdmin: ${isAdmin}, redirecting immediately`);
      
      if (isAdmin) {
        console.log('Admin login detected, redirecting to /admin');
        navigate('/admin', { replace: true });
      } else {
        console.log('Regular user login detected, redirecting to /dashboard');
        navigate('/dashboard', { replace: true });
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setErrorMsg(error.message);
      toast({
        title: 'Login failed',
        description: error.message || 'There was a problem signing in',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...loginForm}>
      <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
        {errorMsg && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm mb-4">
            {errorMsg}
          </div>
        )}
        
        <FormField
          form={loginForm}
          name="email"
          label="Email"
          placeholder="you@example.com"
        />
        
        <FormField
          form={loginForm}
          name="password"
          label="Password"
          placeholder="••••••••"
          type="password"
        />
        
        <Button 
          type="submit" 
          className="w-full"
          disabled={isLoading}
        >
          {isLoading ? 'Logging in...' : 'Log In'}
        </Button>
      </form>
    </Form>
  );
};

export default LoginForm;
